/**
 * Popularity model — how over-represented a number is in the crowd's grids.
 *
 * Multiplicative log-linear model on a uniform base. Coefficients come from
 * Wang, Potter van Loon, van den Assem & van Dolder (2016), Simon (1998) and
 * Roger & Broihanne (2007). They describe Dutch and British populations, not
 * French ones: the orders of magnitude replicate across countries, the decimals
 * are not guaranteed. Milestone 4 of the roadmap replaces them with an estimate
 * on French payout reports; `Coefficients` is the frozen interface contract.
 *
 * This model is NEVER fed by past draw frequencies. It describes what players
 * pick, not what the machine draws.
 */

export interface Coefficients {
  /** x <= 31: birthday bias */
  date_low: number;
  /** x > 31 */
  date_high: number;
  /** x <= 12: played as a month as well as a day */
  month: number;
  odd: number;
  prime: number;
  /** multiples of 10 */
  round: number;
  seven: number;
  thirteen: number;
  /** the four highest numbers of the matrix */
  high_tail: number;
}

export const DEFAULT_COEF: Readonly<Coefficients> = Object.freeze({
  date_low: 1.28,
  date_high: 0.72,
  month: 1.1,
  odd: 1.06,
  prime: 1.05,
  round: 0.9,
  seven: 1.25,
  thirteen: 1.12,
  high_tail: 0.92,
});

const PRIMES = new Set([2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47]);

export function popularityWeight(x: number, nmax: number, coef: Coefficients): number {
  let w = x <= 31 ? coef.date_low : coef.date_high;
  if (x <= 12) w *= coef.month;
  if (x % 2 === 1) w *= coef.odd;
  if (PRIMES.has(x)) w *= coef.prime;
  if (x % 10 === 0) w *= coef.round;
  if (x === 7) w *= coef.seven;
  if (x === 13) w *= coef.thirteen;
  if (x >= nmax - 3) w *= coef.high_tail;
  return w;
}

const profileCache = new Map<string, readonly number[]>();

function cacheKey(nmax: number, coef: Coefficients): string {
  return `${nmax}|${coef.date_low},${coef.date_high},${coef.month},${coef.odd},${coef.prime},${coef.round},${coef.seven},${coef.thirteen},${coef.high_tail}`;
}

/**
 * Popularity of each number 1..nmax, normalised so the mean is exactly 1.00.
 * 1.00 means "played as often as an average number".
 */
export function popularityProfile(
  nmax: number,
  coef: Coefficients = DEFAULT_COEF,
): readonly number[] {
  const key = cacheKey(nmax, coef);
  const hit = profileCache.get(key);
  if (hit !== undefined) return hit;

  const w: number[] = [];
  for (let i = 1; i <= nmax; i++) w.push(popularityWeight(i, nmax, coef));
  let sum = 0;
  for (const x of w) sum += x;
  const mean = sum / w.length;
  const profile = Object.freeze(w.map((x) => x / mean));
  profileCache.set(key, profile);
  return profile;
}

/**
 * Popularity index of a whole combination. 1.00 = as popular as an average
 * combination; below 1 = less played than average, which is the point.
 */
export function comboPopularity(
  combo: readonly number[],
  nmax: number,
  coef: Coefficients = DEFAULT_COEF,
): number {
  const p = popularityProfile(nmax, coef);
  let s = 0;
  for (const x of p) s += x;
  let r = 1;
  for (const x of combo) {
    const v = p[x - 1];
    if (v === undefined) throw new RangeError(`numéro hors bornes : ${x}`);
    r *= (v / s) * nmax;
  }
  return r;
}
